import { Auth } from "aws-amplify";
import { POST_GOOGLE_AUTH_REDIRECT } from "lookup";
import { useEffect, useRef } from "react";

function _shouldAutoGoogleSignIn() {
  // For the scenario - foobar.com?autoGoogleSignIn=true
  const searchParams = new URLSearchParams(window.location.search);
  // For the scenario - foobar.com/#/?autoGoogleSignIn=true
  const hash = window.location.hash;
  const [hashPath = "", hashQuery = ""] = hash.slice(1).split("?");
  const hashParams = new URLSearchParams(hashQuery);

  const autoGoogleSignIn =
    searchParams.get("autoGoogleSignIn") || hashParams.get("autoGoogleSignIn");

  return [autoGoogleSignIn, hashPath, hashParams];
}

function _isIframeContext() {
  if (
    process.env.REACT_APP_VERCEL_ENV !== "production" &&
    new URLSearchParams(window.location.search).get("_testIframe") === "true"
  ) {
    return true;
  }
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export default function AutoGoogleSignIn() {
  const containerRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [autoGoogleSignIn, hashPath, hashParams] =
        _shouldAutoGoogleSignIn();

      let user = null;
      try {
        user = await Auth.currentUserInfo();
      } catch {
        user = null;
      }

      if (!user && autoGoogleSignIn) {
        if (_isIframeContext()) {
          // We are inside an iframe - open a popup for authentication
          window.open(window.location.href, "_blank", "width=500,height=600");
        } else if (window.location.search.indexOf("code") === -1) {
          // We are not returning from an oauth flow - safe to trigger the oauth flow

          if (hashPath.length > 0) {
            // Save current hashPath - to redirect later
            // We don't save the autoGoogleSignIn param - since we don't want to run that flow again
            hashParams.delete("autoGoogleSignIn");
            const cleanedPath =
              hashParams.toString().length > 0
                ? `${hashPath}?${hashParams.toString()}`
                : `${hashPath}`;
            window.sessionStorage.setItem(
              POST_GOOGLE_AUTH_REDIRECT,
              cleanedPath
            );
          }

          // Set DOM attribute synchronously so tests can observe before navigation
          if (containerRef.current) {
            containerRef.current.setAttribute("data-triggered", "true");
          }
          Auth.federatedSignIn({
            provider: "Google",
          });
        }
      }
    })();
  }, []);

  const handleOnClick = () => {
    if (_isIframeContext()) {
      // We are inside an iframe - open a popup for authentication
      const [autoGoogleSignIn] = _shouldAutoGoogleSignIn();

      const url = new URL(window.location.href);
      let newUrl;

      if (!autoGoogleSignIn) {
        // A possibility if the user has signed out, but still within an iframe
        url.searchParams.set("autoGoogleSignIn", "true");
      }

      newUrl = url.toString();

      window.open(newUrl, "_blank", "width=500,height=600");
    } else {
      Auth.federatedSignIn({
        provider: "Google",
      });
    }
  };

  return (
    <div
      ref={containerRef}
      data-testid="auto-google-sign-in"
      data-triggered="false"
      className="flex flex-col pt-8 gap-4 w-10/12"
    >
      <button
        data-testid="google-sign-in-button"
        className="border border-slate-400 p-2 flex gap-2 items-center rounded-md w-full justify-center"
        onClick={handleOnClick}
      >
        <svg
          aria-label="Google icon"
          className="w-4 h-4"
          viewBox="0 0 256 262"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid"
        >
          <path
            d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
            fill="#4285F4"
          ></path>
          <path
            d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
            fill="#34A853"
          ></path>
          <path
            d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"
            fill="#FBBC05"
          ></path>
          <path
            d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
            fill="#EB4335"
          ></path>
        </svg>
        <span>Sign In with Google</span>
      </button>
      <hr className="text-slate-300" />
    </div>
  );
}
